import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import type { SenderSummary } from '@/lib/services/campaigns';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Toggle } from '@/components/ui/toggle';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { TableFooter } from '@/components/data-table';
import { EnvelopeIcon, PaperAirplaneIcon } from '@heroicons/react/24/solid';
import { GmailPreviewModal } from '@/components/ui/gmail-preview-modal';
import { renderMergeTags, splitName, type MergeTagValues } from '@/lib/merge-tags';
import { DialogCloseButton } from '@/components/ui/dialog-close-button';
import { EmailEditor } from '@/components/ui/email-editor';
import { useEmailAccounts } from '@/lib/hooks/useEmailAccount';

const fetcher = (url: string) => fetch(url, { credentials: 'include' }).then((res) => res.json());

type EmailDraft = {
  id: number;
  campaignId: string;
  campaignLeadId: string;
  campaignStepId: string;
  stepIndex: number;
  recipientEmail: string;
  recipientName: string | null;
  subject: string;
  content: string;
  status: string;
  approvedAt: string | null;
  createdAt: string;
  campaignName: string;
  senderEmail: string | null;
  lead: {
    firstName: string | null;
    lastName: string | null;
    company: string | null;
    email: string | null;
    linkedinUrl: string | null;
  } | null;
  /** When the mailer has actually sent this draft (drafts stay 'approved' in the DB after sending). */
  sentAt: string | null;
};

// One row per email the mailer actually sent (campaign_emails).
type SentEmail = {
  id: string;
  stepIndex: number;
  campaignStepId: string | null;
  subject: string | null;
  content: string | null;
  messageId: string | null;
  status: string | null;
  sentAt: string;
  recipientEmail: string | null;
  recipientName: string | null;
  recipientCompany: string | null;
  senderEmail: string | null;
};

type SendsView = 'approvals' | 'queued' | 'sent';

function stripHtml(html: string): string {
  let cleaned = html.replace(/^```html?\s*\n?/i, '').replace(/\n?```\s*$/i, '');
  // textContent concatenates block elements with nothing between them, which
  // ran a draft's closing question straight into its sign-off in the card
  // snippet ("…word of mouth?Sarah"). Break blocks with a space first.
  cleaned = cleaned.replace(/<\/(p|div|li|h[1-6])>/gi, ' ').replace(/<br\s*\/?>/gi, ' ');
  const div = document.createElement('div');
  div.innerHTML = cleaned;
  return (div.textContent || div.innerText || '').replace(/\s+/g, ' ').trim();
}

function stripFences(html: string): string {
  return html.replace(/^```html?\s*\n?/i, '').replace(/\n?```\s*$/i, '');
}

const HOLD_REASON_LABEL: Record<string, string> = {
  sender_capped_today: 'mailbox at its daily limit',
  sender_unhealthy: 'mailbox paused for health',
  sender_disconnected: 'mailbox disconnected',
  sender_removed_from_pool: 'mailbox removed from campaign',
  campaign_daily_limit: 'campaign daily limit',
  outside_send_window: 'outside sending hours',
  job_changed: 'changed company, email needs re-verifying',
};

export function SendsTab({
  agentId,
  campaignId,
  senderEmails = [],
  senderSummary,
  inCard = false,
}: {
  agentId: string | null | undefined;
  campaignId: string;
  senderEmails?: string[];
  senderSummary?: SenderSummary;
  /** In the campaign editor's flush panel the tables bleed through its page
      inset; on a plain page they bleed through the gutter. */
  inCard?: boolean;
}) {
  const navigate = useNavigate();
  const [view, setView] = useState<SendsView>('approvals');

  // Campaign-scoped fetch — works whether or not the campaign has a linked agent.
  const { data: drafts, isLoading, mutate: mutateDrafts } = useSWR<EmailDraft[]>(
    campaignId ? `/api/campaigns/${campaignId}/email-drafts` : null,
    fetcher,
    { refreshInterval: 15_000 }
  );
  // Drafts, sends and the summary line all refresh every 15 seconds so a live
  // campaign's activity shows up without a reload (the mailer ticks about once
  // a minute and syncs replies once a minute).
  const { data: sends, isLoading: sendsLoading } = useSWR<SentEmail[]>(
    campaignId ? `/api/campaigns/${campaignId}/sends` : null,
    fetcher,
    { refreshInterval: 15_000 }
  );
  // The summary line reads the campaign endpoint under its own cache key, so
  // polling it never touches the editor's copy of the campaign.
  const { data: liveCampaign } = useSWR<{ senderSummary?: SenderSummary }>(
    campaignId ? ['sends-tab-sender-summary', campaignId] : null,
    () => fetcher(`/api/campaigns/${campaignId}`),
    { refreshInterval: 15_000 }
  );
  const summary = liveCampaign?.senderSummary ?? senderSummary;
  // The approve/rewrite/regenerate/cancel routes are still under /agents/:id/...
  // but they don't actually validate the path id — they look up by draftId.
  // Using a falsy-safe placeholder keeps the URL valid when no agent is linked.
  const draftActionsAgentId = agentId ?? 'none';
  // Display name of the connected sender account, so previews show the real
  // From line ("Joel Darlington <sarah@…>") rather than the address alone.
  const { emailAccounts } = useEmailAccounts();
  const senderNameFor = (email: string | null | undefined): string | undefined => {
    if (!email) return undefined;
    const account = emailAccounts.find(a => a.email.toLowerCase() === email.toLowerCase());
    return account?.displayName || undefined;
  };
  // The values the mailer will substitute at send time, so every rendering of
  // a draft here shows the email as it will arrive rather than raw tags.
  const mergeValuesFor = (draft: EmailDraft): MergeTagValues => {
    const recipient = splitName(draft.recipientName);
    const sender = splitName(senderNameFor(draft.senderEmail));
    return {
      firstName: draft.lead?.firstName || recipient.first,
      lastName: draft.lead?.lastName || recipient.last,
      company: draft.lead?.company,
      email: draft.lead?.email || draft.recipientEmail,
      linkedinUrl: draft.lead?.linkedinUrl,
      senderFirstName: sender.first,
      senderLastName: sender.last,
    };
  };
  const [previewDraft, setPreviewDraft] = useState<EmailDraft | null>(null);
  const [previewSend, setPreviewSend] = useState<SentEmail | null>(null);
  const [rewriteDraft, setRewriteDraft] = useState<EmailDraft | null>(null);
  const [rewriteContent, setRewriteContent] = useState('');
  const [rewriteInstructions, setRewriteInstructions] = useState('');
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [isRegenerating, setIsRegenerating] = useState(false);

  const openRewrite = (draft: EmailDraft) => {
    setRewriteDraft(draft);
    setRewriteContent(stripFences(draft.content));
    setRewriteInstructions('');
  };

  const handleApprove = async (draftId: number) => {
    setActionLoading(draftId);
    try {
      await fetch(`/api/agents/${draftActionsAgentId}/email-drafts/${draftId}/approve`, {
        method: 'PATCH',
        credentials: 'include',
      });
      mutateDrafts();
      toast.success('Email approved');
    } catch {
      toast.error('Failed to approve');
    }
    setActionLoading(null);
  };

  const handleSaveRewrite = async () => {
    if (!rewriteDraft) return;
    setActionLoading(rewriteDraft.id);
    try {
      await fetch(`/api/agents/${draftActionsAgentId}/email-drafts/${rewriteDraft.id}/rewrite`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: rewriteContent }),
      });
      mutateDrafts();
      setRewriteDraft(null);
      toast.success('Email updated');
    } catch {
      toast.error('Failed to save');
    }
    setActionLoading(null);
  };

  const handleRegenerateWithInstructions = async () => {
    if (!rewriteDraft) return;
    setIsRegenerating(true);
    try {
      const res = await fetch(`/api/agents/${draftActionsAgentId}/email-drafts/${rewriteDraft.id}/regenerate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ instructions: rewriteInstructions || undefined }),
      });
      const updated = await res.json();
      if (updated.content) {
        setRewriteContent(updated.content);
        setRewriteInstructions('');
        toast.success('Email regenerated');
      }
      mutateDrafts();
    } catch {
      toast.error('Failed to regenerate');
    }
    setIsRegenerating(false);
  };

  const handleCancelSequence = async (draftId: number) => {
    if (!confirm('Cancel the entire email sequence for this lead? They will not receive any more emails.')) return;
    setActionLoading(draftId);
    try {
      await fetch(`/api/agents/${draftActionsAgentId}/email-drafts/${draftId}/cancel-sequence`, {
        method: 'DELETE',
        credentials: 'include',
      });
      mutateDrafts();
      toast.success('Sequence cancelled');
    } catch {
      toast.error('Failed to cancel');
    }
    setActionLoading(null);
  };

  const draftsList = Array.isArray(drafts) ? drafts : [];
  const pendingDrafts = draftsList.filter(d => d.status === 'pending_approval');
  // Rejected drafts still need a decision, so they stay on the approvals tab.
  const needsActionDrafts = draftsList.filter(d => d.status === 'pending_approval' || d.status === 'rejected');
  // Approved but not yet picked up by the mailer. Once sent, a draft leaves
  // this list and the send itself appears on the Sent tab.
  const queuedDrafts = draftsList.filter(d => d.status === 'approved' && !d.sentAt);
  const sendsList = Array.isArray(sends) ? sends : [];

  const renderDraftEmptyState = (headline: string, hint: string) => (
    <div className="flex-1 flex flex-col items-center justify-center text-center text-foreground
      pb-[calc(var(--spacing-page-chrome)+3rem+1rem-var(--spacing-gutter))]">
      <EnvelopeIcon className="h-10 w-10 mx-auto mb-3" />
      <p>{headline}</p>
      <p className="text-xs mt-1">{hint}</p>
    </div>
  );

  const renderDraftList = (list: EmailDraft[], header: string | null) => (
    <div className="space-y-3">
        {header && (
          <p className="text-sm text-muted-foreground mb-2 ml-4">{header}</p>
        )}
        {list.map(draft => (
          <div
            key={draft.id}
            className={`relative p-6 rounded-inner border bg-card shadow-md transition-colors ${
              draft.status === 'approved' ? 'bg-success-surface/30' :
              draft.status === 'rejected' ? 'border-danger-border bg-danger-surface/30 opacity-60' :
              ''
            }`}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-sm truncate">
                    {draft.recipientName || draft.recipientEmail}
                  </span>
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0 shrink-0">
                    Step {draft.stepIndex + 1}
                  </Badge>
                  <Badge
                    variant={draft.status === 'rejected' ? 'destructive' : 'secondary'}
                    className={`text-[10px] px-1.5 py-0 shrink-0 ${
                      draft.status === 'approved' ? 'bg-success-surface-strong text-success-foreground-strong' :
                      draft.status === 'pending_approval' ? 'bg-warning-surface-strong text-warning-foreground-strong' : ''
                    }`}
                  >
                    {draft.status === 'pending_approval' ? 'Pending' : draft.status === 'approved' ? 'Queued' : 'Rejected'}
                  </Badge>
                </div>

                <p className="text-base font-medium truncate mt-3">{draft.subject}</p>

                <div>
                  <p className="text-xs text-content-strong truncate mt-1 mb-3">
                    {renderMergeTags(stripHtml(draft.content), mergeValuesFor(draft)).slice(0, 200)}...
                  </p>
                </div>

                <div className="flex items-center gap-1.5 flex-nowrap overflow-hidden">
                  <span className="text-xs text-content-strong bg-surface-subtle rounded-full px-2.5 py-1 whitespace-nowrap shrink-0">{draft.recipientEmail}</span>
                  <span className="text-xs text-content-strong bg-surface-subtle rounded-full px-2.5 py-1 whitespace-nowrap truncate">{draft.campaignName}</span>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <Button variant="secondary" size="sm" onClick={() => setPreviewDraft(draft)}>
                  Show Preview
                </Button>
                <Button variant="secondary" size="sm" onClick={() => openRewrite(draft)}>
                  Rewrite
                </Button>
                {draft.status === 'pending_approval' && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => handleApprove(draft.id)}
                    disabled={actionLoading === draft.id}
                  >
                    Approve
                  </Button>
                )}
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => navigate(`/dashboard/campaigns/${campaignId}/edit?tab=sequence&step=${draft.campaignStepId}`)}
                >
                  View Sequence Step
                </Button>
              </div>
            </div>
            <div className="flex justify-end mt-2 px-1">
              <Button
                variant="ghost"
                size="sm"
                className="text-xs text-muted-foreground hover:text-danger-text"
                onClick={() => handleCancelSequence(draft.id)}
                disabled={actionLoading === draft.id}
              >
                Cancel Sequence
              </Button>
            </div>
          </div>
        ))}
      </div>
  );

  const renderApprovals = () => {
    if (isLoading) return <Skeleton className="h-40 w-full rounded-xl" />;
    if (needsActionDrafts.length === 0) {
      return renderDraftEmptyState(
        'No emails need approval.',
        'New drafts land here as the campaign generates them.',
      );
    }
    const n = pendingDrafts.length;
    return renderDraftList(
      needsActionDrafts,
      n > 0 ? `${n} email${n !== 1 ? 's' : ''} pending approval` : null,
    );
  };

  const renderQueued = () => {
    if (isLoading) return <Skeleton className="h-40 w-full rounded-xl" />;
    if (queuedDrafts.length === 0) {
      return renderDraftEmptyState(
        'Nothing queued.',
        'Approved emails wait here until the mailer sends them.',
      );
    }
    // Same table treatment as Sent — these are the same emails one stage
    // earlier, so they get the same shape. Rows open the draft preview.
    return (
      <div className="relative">
        <div className={inCard ? '-mx-page-inset' : '-mx-gutter'}>
        <Table>
          <TableHeader>
            <TableRow className="h-[45px]">
              <TableHead>Recipient</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Company</TableHead>
              <TableHead>Subject</TableHead>
              <TableHead className="text-right">Step</TableHead>
              <TableHead className="text-right">Approved</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {queuedDrafts.map(draft => {
              const approvedAt = draft.approvedAt ? new Date(draft.approvedAt) : null;
              return (
                <TableRow
                  key={draft.id}
                  className="h-[53px] cursor-pointer"
                  onClick={() => setPreviewDraft(draft)}
                >
                  <TableCell>
                    <div className="font-normal truncate max-w-[220px]">
                      {draft.recipientName || draft.recipientEmail}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-muted-foreground truncate max-w-[260px]">{draft.recipientEmail}</div>
                  </TableCell>
                  <TableCell>
                    <div className="truncate max-w-[220px]">{draft.lead?.company}</div>
                  </TableCell>
                  <TableCell>
                    <div className="truncate max-w-[320px]">
                      {renderMergeTags(draft.subject || '(no subject)', mergeValuesFor(draft))}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">{draft.stepIndex + 1}</TableCell>
                  <TableCell className="text-right">
                    {approvedAt ? (
                      <span title={formatDistanceToNow(approvedAt, { addSuffix: true })}>
                        {format(approvedAt, 'd MMM yyyy, HH:mm')}
                      </span>
                    ) : null}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        </div>
        <TableFooter surface="card">
          <span className="text-sm text-foreground whitespace-nowrap">
            <span className="font-bold">{queuedDrafts.length}</span> queued
          </span>
        </TableFooter>
      </div>
    );
  };

  const renderSent = () => {
    if (sendsLoading) return <Skeleton className="h-40 w-full rounded-xl" />;

    if (sendsList.length === 0) {
      return (
        <div className="flex-1 flex flex-col items-center justify-center text-center text-foreground
          pb-[calc(var(--spacing-page-chrome)+3rem+1rem-var(--spacing-gutter))]">
          <PaperAirplaneIcon className="h-10 w-10 mx-auto mb-3" />
          <p>No emails sent yet.</p>
          <p className="text-xs mt-1">Sends appear here as soon as the campaign delivers them.</p>
        </div>
      );
    }

    // Same table treatment as the campaigns index: single-line cells, muted
    // headers, numbers and dates right-aligned, rows clickable. Row heights are
    // pinned to the index's (45px header, 53px rows); text-only cells would
    // otherwise collapse to the line height and look squashed.
    return (
      <div className="relative">
        <div className={inCard ? '-mx-page-inset' : '-mx-gutter'}>
        <Table>
          <TableHeader>
            <TableRow className="h-[45px]">
              <TableHead>Recipient</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Company</TableHead>
              <TableHead>Subject</TableHead>
              <TableHead className="text-right">Step</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Sent</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sendsList.map(send => {
              const sentAt = new Date(send.sentAt);
              const status = send.status || 'sent';
              const failed = status === 'failed' || status === 'bounced';
              return (
                <TableRow
                  key={send.id}
                  className="h-[53px] cursor-pointer"
                  onClick={() => setPreviewSend(send)}
                >
                  <TableCell>
                    <div className="font-normal truncate max-w-[220px]">
                      {send.recipientName || send.recipientEmail || 'Unknown recipient'}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-muted-foreground truncate max-w-[260px]">{send.recipientEmail}</div>
                  </TableCell>
                  <TableCell>
                    <div className="truncate max-w-[220px]">{send.recipientCompany}</div>
                  </TableCell>
                  <TableCell>
                    <div className="truncate max-w-[320px]">{send.subject || '(no subject)'}</div>
                  </TableCell>
                  <TableCell className="text-right">{send.stepIndex + 1}</TableCell>
                  <TableCell className={failed ? 'text-danger-foreground' : 'text-success-foreground-strong'}>
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </TableCell>
                  <TableCell className="text-right">
                    <span title={formatDistanceToNow(sentAt, { addSuffix: true })}>
                      {format(sentAt, 'd MMM yyyy, HH:mm')}
                    </span>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        </div>
        <TableFooter surface="card">
          <span className="text-sm text-foreground whitespace-nowrap">
            <span className="font-bold">{sendsList.length}</span> send{sendsList.length !== 1 ? 's' : ''}
          </span>
        </TableFooter>
      </div>
    );
  };

  // One line on what the pool did today and who is waiting, so a held lead
  // is never mistaken for a campaign that has simply finished.
  const heldEntries = Object.entries(summary?.held.byReason ?? {});
  const poolLine = senderEmails.length > 0 ? (
    <p className="text-sm text-muted-foreground tabular-nums whitespace-nowrap">
      Sending from {senderEmails.length} mailbox{senderEmails.length === 1 ? '' : 'es'}
      {summary ? ` · ${summary.sentToday} sent today` : ''}
      {summary && summary.held.total > 0 ? (
        <span className="text-warning-foreground">
          {' · '}{summary.held.total} held
          {heldEntries.length > 0
            ? ` (${heldEntries.map(([reason, n]) => `${n} ${HOLD_REASON_LABEL[reason] ?? reason}`).join(', ')})`
            : ''}
        </span>
      ) : null}
    </p>
  ) : null;

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="flex items-center justify-between mb-header-gap">
        <Toggle<SendsView>
          options={[
            { value: 'approvals', label: 'Approvals Needed', alert: pendingDrafts.length },
            { value: 'queued', label: 'Queued', number: queuedDrafts.length || undefined },
            { value: 'sent', label: 'Sent', number: sendsList.length || undefined },
          ]}
          value={view}
          onChange={setView}
        />
        {poolLine}
      </div>

      {view === 'sent' ? renderSent() : view === 'queued' ? renderQueued() : renderApprovals()}

      <GmailPreviewModal
        isOpen={!!previewDraft}
        onClose={() => setPreviewDraft(null)}
        subject={previewDraft ? renderMergeTags(previewDraft.subject || '', mergeValuesFor(previewDraft)) : ''}
        body={previewDraft ? renderMergeTags(stripFences(previewDraft.content), mergeValuesFor(previewDraft)) : ''}
        fromEmail={previewDraft?.senderEmail || 'sender@example.com'}
        fromName={senderNameFor(previewDraft?.senderEmail)}
        toEmail={previewDraft?.recipientEmail || ''}
        toName={previewDraft?.recipientName || undefined}
      />

      <GmailPreviewModal
        isOpen={!!previewSend}
        onClose={() => setPreviewSend(null)}
        subject={previewSend?.subject || ''}
        body={previewSend?.content || ''}
        fromEmail={previewSend?.senderEmail || 'sender@example.com'}
        fromName={senderNameFor(previewSend?.senderEmail)}
        toEmail={previewSend?.recipientEmail || ''}
        toName={previewSend?.recipientName || undefined}
      />

      {rewriteDraft && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-scrim/50" onClick={() => setRewriteDraft(null)}>
          <div className="relative w-full max-w-3xl bg-popover rounded-panel" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between px-8 pt-6 pb-3">
                <div>
                  <h3 className="text-lg font-medium">Rewrite Email</h3>
                  <p className="text-sm text-muted-foreground">
                    To: {rewriteDraft.recipientName || rewriteDraft.recipientEmail}
                  </p>
                </div>
                <DialogCloseButton onClick={() => setRewriteDraft(null)} />
              </div>

              <div className="px-6 flex flex-col" style={{ height: 'min(calc(80vh - 280px), 420px)' }}>
                <EmailEditor
                  value={rewriteContent}
                  onChange={setRewriteContent}
                  label=""
                  placeholder="Write your email..."
                />
              </div>

              <div className="px-6 pt-3 flex gap-2">
                <Input
                  value={rewriteInstructions}
                  onChange={e => setRewriteInstructions(e.target.value)}
                  placeholder="Optional instructions for AI, e.g. 'make it shorter'"
                  className="flex-1 !mb-0"
                />
                <Button
                  variant="primary"
                  className="h-12 shrink-0"
                  onClick={handleRegenerateWithInstructions}
                  disabled={isRegenerating}
                >
                  {isRegenerating ? 'Regenerating...' : 'Regenerate with AI'}
                </Button>
              </div>

              <div className="flex justify-end gap-2 px-6 py-4">
                <Button variant="secondary" onClick={() => setRewriteDraft(null)}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleSaveRewrite}>
                  Save
                </Button>
              </div>
          </div>
        </div>
      )}
    </div>
  );
}
